import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/api/client";
import type { components } from "@dolfin/api-admin";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Spinner } from "@/components/ui/spinner";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { PaginationBar } from "@/components/PaginationBar";
import { useCursorPagination } from "@/hooks/useCursorPagination";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";
import { formatCurrency, formatDateTime, formatNumber } from "@/lib/format";
import { num } from "@/lib/matching";

type MatchGroupSummary = components["schemas"]["MatchGroupSummaryResponse"];
type MatchGroupStatus = components["schemas"]["MatchGroupStatus"];

const ANY = "any";

const STATUSES: { id: MatchGroupStatus; label: string }[] = [
  { id: "Open", label: "Open" },
  { id: "Variance", label: "Variance" },
  { id: "Resolved", label: "Resolved" },
  { id: "Synced", label: "Synced" },
];

// Base columns; the scope-dependent ID columns are added on top for the empty-state colSpan.
const BASE_COLUMNS = 8;

interface MatchGroupsTableProps {
  /** Scope. Fixing it drops the client filter and both ID columns - an organisation implies its client. */
  organisationId?: string;
  /** Scope. Fixing it drops the client filter and the client column, but keeps the organisation filter. */
  clientId?: string;
}

function statusVariant(
  status: MatchGroupStatus | undefined,
): "default" | "secondary" | "outline" | "destructive" {
  switch (status) {
    case "Resolved":
    case "Synced":
      return "default";
    case "Variance":
      return "destructive";
    default:
      return "outline";
  }
}

// Rows link to the detail page: what the engine offered and didn't take costs a scoring pass per group.
// The invoice filter is a substring match on purpose: quoted numbers often differ by a prefix or space.
export function MatchGroupsTable({ organisationId, clientId }: MatchGroupsTableProps) {
  const pagination = useCursorPagination({ initialLimit: 20 });

  // One flag drives both the filter and the ID column; an organisation implies its client, so org scope drops both.
  const showOrganisation = !organisationId;
  const showClient = !organisationId && !clientId;

  const [organisationInput, setOrganisationInput] = useState("");
  const [clientInput, setClientInput] = useState("");
  const [invoiceInput, setInvoiceInput] = useState("");
  const [billInput, setBillInput] = useState("");
  const [status, setStatus] = useState<MatchGroupStatus | typeof ANY>(ANY);

  const organisationFilter = useDebouncedValue(organisationInput).trim();
  const clientFilter = useDebouncedValue(clientInput).trim();
  const invoiceNumber = useDebouncedValue(invoiceInput).trim();
  const billId = useDebouncedValue(billInput).trim();

  const organisationQuery = organisationId ?? organisationFilter;
  const clientQuery = clientId ?? clientFilter;
  const statusQuery = status === ANY ? undefined : status;

  const [groups, setGroups] = useState<MatchGroupSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Reset the cursor during render, not in an effect: an effect would fire the fetch once on the stale cursor.
  const filterKey = [organisationQuery, clientQuery, invoiceNumber, billId, statusQuery].join("|");
  const lastFilterKey = useRef(filterKey);
  if (lastFilterKey.current !== filterKey) {
    lastFilterKey.current = filterKey;
    pagination.reset();
  }

  useEffect(() => {
    let cancelled = false;

    async function fetchGroups() {
      setLoading(true);
      setError(null);
      const { data, error: apiError } = await api.GET("/admin/match-groups", {
        params: {
          query: {
            OrganisationId: organisationQuery || undefined,
            ClientId: clientQuery || undefined,
            Status: statusQuery,
            BillId: billId || undefined,
            InvoiceNumber: invoiceNumber || undefined,
            Cursor: pagination.cursor,
            Limit: pagination.limit,
          },
        },
      });
      if (cancelled) return;
      if (apiError) {
        setError("Failed to load match groups.");
      } else {
        setGroups(data?.data ?? []);
        pagination.setPage({
          nextCursor: data?.meta?.cursors?.next,
          previousCursor: data?.meta?.cursors?.previous,
        });
      }
      setLoading(false);
    }

    fetchGroups();
    return () => {
      cancelled = true;
    };
  }, [
    organisationQuery,
    clientQuery,
    statusQuery,
    billId,
    invoiceNumber,
    pagination.cursor,
    pagination.limit,
  ]);

  const columnCount = BASE_COLUMNS + (showClient ? 1 : 0) + (showOrganisation ? 1 : 0);

  return (
    <div>
      <div className="mb-4 flex flex-wrap items-center gap-3">
        {showOrganisation && (
          <Input
            value={organisationInput}
            onChange={(e) => setOrganisationInput(e.target.value)}
            placeholder="Organisation ID…"
            spellCheck={false}
            autoComplete="off"
            className="max-w-xs"
          />
        )}
        {showClient && (
          <Input
            value={clientInput}
            onChange={(e) => setClientInput(e.target.value)}
            placeholder="Client ID…"
            spellCheck={false}
            autoComplete="off"
            className="max-w-xs"
          />
        )}
        <Input
          value={invoiceInput}
          onChange={(e) => setInvoiceInput(e.target.value)}
          placeholder="Invoice number…"
          spellCheck={false}
          autoComplete="off"
          className="max-w-xs"
        />
        <Input
          value={billInput}
          onChange={(e) => setBillInput(e.target.value)}
          placeholder="Bill ID…"
          spellCheck={false}
          autoComplete="off"
          className="max-w-xs"
        />
        <Select value={status} onValueChange={(v) => setStatus(v as MatchGroupStatus | typeof ANY)}>
          <SelectTrigger className="w-44">
            <SelectValue placeholder="Any status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ANY}>Any status</SelectItem>
            {STATUSES.map((s) => (
              <SelectItem key={s.id} value={s.id}>
                {s.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {loading && <Spinner className="size-5" />}
      </div>

      {error ? (
        <p className="text-destructive">{error}</p>
      ) : (
        <>
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Bill</TableHead>
                  <TableHead>Supplier</TableHead>
                  {showClient && <TableHead>Client</TableHead>}
                  {showOrganisation && <TableHead>Organisation</TableHead>}
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Net</TableHead>
                  <TableHead className="text-right">Matched</TableHead>
                  <TableHead className="text-right">Variances</TableHead>
                  <TableHead className="text-right">Decisions</TableHead>
                  <TableHead>Created</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {groups.length === 0 && !loading ? (
                  <TableRow>
                    <TableCell colSpan={columnCount} className="text-muted-foreground h-24 text-center">
                      No match groups found.
                    </TableCell>
                  </TableRow>
                ) : (
                  groups.map((group) => (
                    <TableRow key={group.id}>
                      <TableCell>
                        <Link
                          to={`/match-groups/${group.id}`}
                          className="font-medium hover:underline"
                        >
                          {group.invoiceNumber || "(no invoice number)"}
                        </Link>
                        <Link
                          to={`/bills/${group.billId}`}
                          className="text-muted-foreground mt-0.5 block font-mono text-xs hover:underline"
                        >
                          {group.billId.slice(0, 8)}…
                        </Link>
                      </TableCell>
                      <TableCell className="max-w-56 truncate">
                        {/* A supplier name may have no id (never resolved): print it, link only when there's an id. */}
                        {group.supplierId ? (
                          <Link to={`/suppliers/${group.supplierId}`} className="hover:underline">
                            {group.supplierName ?? (
                              <span className="font-mono text-xs">{group.supplierId.slice(0, 8)}…</span>
                            )}
                          </Link>
                        ) : (
                          (group.supplierName ?? <span className="text-muted-foreground">—</span>)
                        )}
                      </TableCell>
                      {showClient && (
                        <TableCell>
                          <Link to={`/clients/${group.clientId}`}>
                            <Badge
                              variant="secondary"
                              className="hover:bg-primary/20 font-mono text-xs"
                            >
                              {group.clientId.slice(0, 8)}…
                            </Badge>
                          </Link>
                        </TableCell>
                      )}
                      {showOrganisation && (
                        <TableCell>
                          <Link to={`/organisations/${group.organisationId}`}>
                            <Badge
                              variant="secondary"
                              className="hover:bg-primary/20 font-mono text-xs"
                            >
                              {group.organisationId.slice(0, 8)}…
                            </Badge>
                          </Link>
                        </TableCell>
                      )}
                      <TableCell>
                        <Badge variant={statusVariant(group.status)}>{group.status}</Badge>
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {formatCurrency(group.billedNet, group.currency)}
                      </TableCell>
                      {/* Lines, not allocations: a line matched to both a PO and a DN is one matched line. */}
                      <TableCell className="text-right tabular-nums">
                        {formatNumber(group.matchedLineCount)}
                        <span className="text-muted-foreground">
                          {" "}
                          / {formatNumber(group.lineCount)}
                        </span>
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {num(group.varianceLineCount) > 0 ? (
                          <span className="text-destructive">
                            {formatNumber(group.varianceLineCount)}
                          </span>
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {num(group.resolutionCount) > 0 ? (
                          formatNumber(group.resolutionCount)
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </TableCell>
                      <TableCell className="text-muted-foreground text-sm">
                        {formatDateTime(group.createdAt)}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>

          <PaginationBar
            hasPrevious={pagination.hasPrevious}
            hasNext={pagination.hasMore}
            isLoading={loading}
            limit={pagination.limit}
            onPrevious={pagination.goPrevious}
            onNext={pagination.goNext}
            onLimitChange={pagination.setLimit}
          />
        </>
      )}
    </div>
  );
}
